import {
    VStack,
    Switch,
    Box,
    Flex,
    Text,
    Tr,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import {
    OrderingSettings,
    OEMSettings,
    FlagMarginBelowEntry,
} from '../../hooks/useGetSettings';
import { useSettingsDraft } from '../../pages/settings/SettingsDraftContext';
import OemOverridesTable, { type OemOverrideColumn } from './OemOverridesTable';
import FlagMarginBelowTable from './FlagMarginBelowTable';
import { normalizeCccOrdering } from './cccOrderingValue';
import {
    SettingsAddItemsModal,
    SettingsCard,
    SettingsNumberField,
    SettingsRow,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from './primitives';
import { Button, Icon, IconButton } from '../../ui';

// const CCC_ORDERING_SEGMENTS = [
//     { value: 'always', label: 'Always', tooltip: 'Order through CCC for every job.' },
//     { value: 'never', label: 'Never', tooltip: 'Never order through the CCC marketplace.' },
//     { value: 'custom', label: 'Custom', tooltip: 'Only order through CCC for the OEMs and carriers named by a rule.' },
// ] as const;

/** The two per-make overrides of the org-wide auto-order margin thresholds. */
const MARGIN_THRESHOLD_COLUMNS: Array<OemOverrideColumn> = [
    { field: 'drpThreshold', label: 'DRP threshold', scale: 100, min: 0, max: 100, unit: '%' },
    { field: 'nonDrpThreshold', label: 'Non-DRP threshold', scale: 100, min: 0, max: 100, unit: '%' },
];

/** Stable order so draft ↔ baseline compares ignore server row order. */
function normalizeFlagMarginByPart(
    entries: Array<FlagMarginBelowEntry> | undefined | null,
): Array<FlagMarginBelowEntry> {
    const list = Array.isArray(entries) ? entries : [];
    const defaultEntry = list.find((e) => e.partType === 'default');
    const overrides = list
        .filter((e) => e.partType !== 'default')
        .slice()
        .sort((a, b) => String(a.partType).localeCompare(String(b.partType)));
    return defaultEntry ? [defaultEntry, ...overrides] : overrides;
}

function normalizeOrdering(settings: OrderingSettings): OrderingSettings {
    return {
        ...settings,
        cccOrdering: normalizeCccOrdering(settings.cccOrdering),
        flagMarginBelowByPart: normalizeFlagMarginByPart(settings.flagMarginBelowByPart),
    };
}

function orderingEquals(a: OrderingSettings, b: OrderingSettings): boolean {
    return JSON.stringify(normalizeOrdering(a)) === JSON.stringify(normalizeOrdering(b));
}

interface OrderingInterface {
    data: OrderingSettings;
    managementSystem: 'ccc' | 'nex';
    /** The oem subdocument, which stores the per-make margin thresholds. */
    oems: Array<OEMSettings>;
    oemOptions: Array<{ _id: string; name: string; }>;
    /** Shop locations an auto-order override can name. */
    locations: Array<{ _id: string; title: string; }>;
}

const Ordering = ({
    data,
    managementSystem,
    oems,
    oemOptions,
    locations,
}: OrderingInterface) => {
    // Every edit on this screen is buffered on the shared settings draft, so
    // the page's save bar sends the ordering and oem changes together in one
    // request.
    const {
        // drafts,
        getDomain,
        setDomain,
        clearDomain,
        getBaseline,
        // isDirty
    } = useSettingsDraft();
    // const navigate = useNavigate();
    const localData = getDomain('ordering', data) ?? data;
    const localOems = getDomain('oem', oems) ?? oems;
    const savedOrdering = getBaseline('ordering', data) ?? data;

    const writeOrdering = (next: OrderingSettings) => {
        if (orderingEquals(next, savedOrdering)) {
            clearDomain('ordering');
        } else {
            setDomain('ordering', next);
        }
    };

    const handleUpdatePreference = (field: keyof OrderingSettings, value: any) => {
        writeOrdering({
            ...localData,
            [field]: value,
        });
    };

    // Per-make thresholds live on the oem subdocument; the org-wide ones they
    // override live here, so this section writes both.
    const handleOemsChange = (next: Array<OEMSettings>) => {
        setDomain('oem', next);
    };

    const persistFlagMarginByPart = (next: Array<FlagMarginBelowEntry>) => {
        writeOrdering({
            ...localData,
            flagMarginBelowByPart: next,
        });
    };

    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [locationSearch, setLocationSearch] = useState('');
    const [pendingLocationIds, setPendingLocationIds] = useState<Set<string>>(new Set());

    const shopLocations = useMemo(
        () => locations.filter((location) => location._id !== 'all'),
        [locations],
    );
    const disabledLocations = useMemo(
        () =>
            [...(localData.autoOrderDisabledLocations ?? [])].sort((a, b) =>
                a.title.localeCompare(b.title),
            ),
        [localData.autoOrderDisabledLocations],
    );
    const disabledLocationPaging = useSubTablePagination(disabledLocations);
    const availableLocations = useMemo(() => {
        const taken = new Set(disabledLocations.map((location) => location._id));
        return shopLocations
            .filter((location) => !taken.has(location._id))
            .sort((a, b) => a.title.localeCompare(b.title));
    }, [shopLocations, disabledLocations]);
    const filteredLocations = useMemo(() => {
        const search = locationSearch.trim().toLowerCase();
        if (!search) return availableLocations;
        return availableLocations.filter((location) => location.title.toLowerCase().includes(search));
    }, [availableLocations, locationSearch]);

    const openLocationModal = () => {
        setLocationSearch('');
        setPendingLocationIds(new Set());
        setIsLocationModalOpen(true);
    };

    const togglePendingLocation = (locationId: string) => {
        setPendingLocationIds((prev) => {
            const next = new Set(prev);
            if (next.has(locationId)) next.delete(locationId);
            else next.add(locationId);
            return next;
        });
    };

    const handleAddDisabledLocations = () => {
        if (pendingLocationIds.size === 0) return;
        const added = shopLocations
            .filter((location) => pendingLocationIds.has(location._id))
            .map((location) => ({ _id: location._id, title: location.title }));
        handleUpdatePreference('autoOrderDisabledLocations', [
            ...(localData.autoOrderDisabledLocations ?? []),
            ...added,
        ]);
        setIsLocationModalOpen(false);
    };

    const handleRemoveDisabledLocation = (locationId: string) => {
        handleUpdatePreference(
            'autoOrderDisabledLocations',
            (localData.autoOrderDisabledLocations ?? []).filter((location) => location._id !== locationId),
        );
    };

    // const cccMode = cccOrderingMode(localData.cccOrdering);
    // Rules are keyed by their position in the list, which is what the rule's
    // own screen is routed by.
    // const cccRules = useMemo(() => toCccOrderingRules(localData.cccOrdering), [localData.cccOrdering]);
    // const cccRuleRows = useMemo(
    //     () => cccRules.map((rule, index) => ({ rule, index })),
    //     [cccRules],
    // );
    // const cccPaging = useSubTablePagination(cccRuleRows);

    // const handleCccOrderingMode = (mode: CccOrderingMode) => {
    //     if (mode === 'custom') {
    //         // Whatever was already configured carries over; switching in with
    //         // nothing configured leaves the table empty, which is what asks for
    //         // the first rule.
    //         handleUpdatePreference('cccOrdering', toStoredCccOrdering(cccRules));
    //         return;
    //     }
    //     handleUpdatePreference('cccOrdering', mode);
    // };

    // const handleRemoveCccRule = (index: number) => {
    //     handleUpdatePreference(
    //         'cccOrdering',
    //         toStoredCccOrdering(cccRules.filter((_, current) => current !== index)),
    //     );
    // };

    // The rule screen renders outside this page's draft and saves against the
    // server's ordering, routed by the server's rule index. Opening it with
    // unsaved edits would drop them and could target a shifted row, so it waits
    // until the page is saved or discarded — except when the only edit is
    // flipping the slider to Custom. Saving a rule writes custom rules anyway,
    // so that edit is carried by the save, and the rule list is still the
    // server's, so every index lines up.
    // const isOnlyCccModePending = useMemo(() => {
    //     const dirtyDomains = Object.keys(drafts);
    //     if (dirtyDomains.length !== 1 || dirtyDomains[0] !== 'ordering') return false;
    //     if (cccMode !== 'custom') return false;
    //     const otherFieldsSaved = orderingEquals(
    //         { ...localData, cccOrdering: savedOrdering.cccOrdering },
    //         savedOrdering,
    //     );
    //     const savedRules = toCccOrderingRules(savedOrdering.cccOrdering);
    //     const rulesSaved =
    //         JSON.stringify(normalizeCccOrdering(cccRules)) === JSON.stringify(normalizeCccOrdering(savedRules));
    //     return otherFieldsSaved && rulesSaved;
    // }, [drafts, cccMode, localData, savedOrdering, cccRules]);
    // const isCccRuleNavBlocked = isDirty && !isOnlyCccModePending;

    // const openCccRule = (path: string) => {
    //     if (isCccRuleNavBlocked) return;
    //     navigate(path);
    // };

    return (
        <VStack spacing={6} align="stretch" pb={16}>
            <SettingsCard
                title="Ordering preferences"
                description="Configure how parts are ordered and filtered."
            >
                <SettingsRow
                    label="Auto ordering"
                    description="When enabled, allow Yaxle to auto order parts when the criteria for auto ordering are met."
                    control={
                        <Switch
                            size="lg"
                            isChecked={localData.allowAutoOrder}
                            onChange={(e) => handleUpdatePreference('allowAutoOrder', e.target.checked)}
                            colorScheme="partbay-blue"
                        />
                    }
                >
                    {localData.allowAutoOrder && (
                        <Box pl={6} borderLeft="2px solid" borderColor="border.subtle">
                            <Flex justify="space-between" align="center" gap={6}>
                                <Box minW={0}>
                                    <Text fontSize="sm" fontWeight="medium" color="fg.default">
                                        Custom overrides
                                    </Text>
                                    <Text fontSize="sm" color="fg.muted" mt={0.5}>
                                        Auto ordering stays off at these locations. Every other
                                        location still uses it.
                                    </Text>
                                </Box>
                                <Button
                                    variant="solid"
                                    colorScheme="brand"
                                    size="sm"
                                    onClick={openLocationModal}
                                    isDisabled={availableLocations.length === 0}
                                >
                                    Add Override
                                </Button>
                            </Flex>
                            <Box mt={3}>
                                <SettingsSubTable
                                    isInset
                                    currentPage={disabledLocationPaging.currentPage}
                                    totalPages={disabledLocationPaging.totalPages}
                                    onPageChange={disabledLocationPaging.setCurrentPage}
                                    head={
                                        <>
                                            <SettingsTh>Location</SettingsTh>
                                            <SettingsTh textAlign="center" w="100px">
                                                Actions
                                            </SettingsTh>
                                        </>
                                    }
                                >
                                    {disabledLocations.length === 0 ? (
                                        <Tr>
                                            <SettingsTd colSpan={2} py={6} color="fg.muted">
                                                No custom overrides — every location uses auto ordering.
                                            </SettingsTd>
                                        </Tr>
                                    ) : (
                                        disabledLocationPaging.pageItems.map((location) => (
                                            <Tr key={location._id}>
                                                <SettingsTd fontWeight="medium" color="fg.default">
                                                    {location.title}
                                                </SettingsTd>
                                                <SettingsTd textAlign="center">
                                                    <IconButton
                                                        aria-label={`Remove ${location.title} override`}
                                                        icon={<Icon name="trash" size="sm" />}
                                                        size="sm"
                                                        variant="ghost"
                                                        colorScheme="red"
                                                        onClick={() => handleRemoveDisabledLocation(location._id)}
                                                    />
                                                </SettingsTd>
                                            </Tr>
                                        ))
                                    )}
                                </SettingsSubTable>
                            </Box>
                        </Box>
                    )}
                </SettingsRow>

                {/* <SettingsRow
                    label="CCC ordering"
                    description="When Yaxle can order through the CCC marketplace."
                    banner={
                        managementSystem !== 'ccc' ? (
                            <Notice tone="warning">
                                For non-CCC workflow customers, we have limited ability to message vendors and track cancellations/returns for CCC orders
                            </Notice>
                        ) : undefined
                    }
                    control={
                        <SettingsSegmentedControl
                            segments={CCC_ORDERING_SEGMENTS}
                            value={cccMode}
                            onChange={handleCccOrderingMode}
                        />
                    }
                /> */}

                {/* The rules behind Custom, as their own block below the row
                    they belong to — the same shape as the per-make override
                    tables further down the page. Adding and editing one is a
                    screen of its own, so the table only lists and removes. */}
                {/* {cccMode === 'custom' && (
                    <SettingsOverridesBlock
                        title="Custom CCC ordering rules"
                        description="PartBay orders through CCC when a job matches one of these rules. Everything else is ordered elsewhere."
                        addButtonText="Add Rule"
                        onAddClick={() => openCccRule('/settings/ordering/new')}
                        isDisabled={isCccRuleNavBlocked}
                    >
                        {isCccRuleNavBlocked && (
                            <Box mb={3}>
                                <Notice tone="info">
                                    Save or discard your changes to add or edit a rule.
                                </Notice>
                            </Box>
                        )}
                        <SettingsSubTable
                            isInset
                            currentPage={cccPaging.currentPage}
                            totalPages={cccPaging.totalPages}
                            onPageChange={cccPaging.setCurrentPage}
                            head={
                                <>
                                    <SettingsTh>OEMs</SettingsTh>
                                    <SettingsTh>Insurance companies</SettingsTh>
                                    <SettingsTh textAlign="center" w="100px">
                                        Actions
                                    </SettingsTh>
                                </>
                            }
                        >
                            {cccRuleRows.length === 0 ? (
                                <Tr>
                                    <SettingsTd colSpan={3} py={6} color="fg.muted">
                                        No CCC ordering rules configured. Click "Add Rule" to create
                                        one.
                                    </SettingsTd>
                                </Tr>
                            ) : (
                                cccPaging.pageItems.map(({ rule, index }) => (
                                    <Tr key={index}>
                                        <SettingsTd fontWeight="medium" color="fg.default">
                                            {cccScopeLabel(rule.makes, 'All OEMs')}
                                        </SettingsTd>
                                        <SettingsTd color="fg.default">
                                            {cccScopeLabel(rule.carriers, 'All insurance')}
                                        </SettingsTd>
                                        <SettingsTd textAlign="center">
                                            <SettingsRowActions
                                                onEdit={() => openCccRule(`/settings/ordering/${index}`)}
                                                isEditDisabled={isCccRuleNavBlocked}
                                                onDelete={() => handleRemoveCccRule(index)}
                                                editLabel="Edit rule"
                                                deleteLabel="Remove rule"
                                            />
                                        </SettingsTd>
                                    </Tr>
                                ))
                            )}
                        </SettingsSubTable>
                    </SettingsOverridesBlock>
                )} */}

                <SettingsRow
                    label="Disable auto ordering for Customer/Self Pay"
                    description="When enabled, Customer/Self Pay orders cannot be auto ordered."
                    control={
                        <Switch
                            size="lg"
                            isChecked={!!localData.disableAutoOrderCustomerPay}
                            onChange={(e) => handleUpdatePreference('disableAutoOrderCustomerPay', e.target.checked)}
                            colorScheme="partbay-blue"
                        />
                    }
                />

                <SettingsRow
                    label="Flag non-preferred vendors"
                    description="Flag selections from non-preferred vendors, and exclude from auto ordering."
                    control={
                        <Switch
                            size="lg"
                            isChecked={localData.flagNonPreferredVendors}
                            onChange={(e) => handleUpdatePreference('flagNonPreferredVendors', e.target.checked)}
                            colorScheme="partbay-blue"
                        />
                    }
                />

                <SettingsRow
                    label="Auto accept supplier changes"
                    description="When enabled, automatically accept part changes made by suppliers without requiring manual review."
                    control={
                        <Switch
                            size="lg"
                            isChecked={localData.autoAcceptSupplierChanges}
                            onChange={(e) => handleUpdatePreference('autoAcceptSupplierChanges', e.target.checked)}
                            colorScheme="partbay-blue"
                        />
                    }
                >
                    {/* Which changes get auto accepted only matters once auto
                        accept is on, so it nests inside that setting. */}
                    {localData.autoAcceptSupplierChanges && (
                        <Box pl={6} borderLeft="2px solid" borderColor="gray.200">
                            <Flex justify="space-between" align="center" gap={6}>
                                <Box minW={0}>
                                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                        Auto accept non-price changes only
                                    </Text>
                                    <Text fontSize="sm" color="gray.500" mt={0.5}>
                                        When enabled, only changes that do not affect price are
                                        auto accepted, such as delivery dates and part numbers.
                                        Price changes will still require manual review.
                                    </Text>
                                </Box>
                                <Switch
                                    size="lg"
                                    colorScheme="partbay-blue"
                                    isChecked={!!localData.autoAcceptNonPriceSupplierChangesOnly}
                                    onChange={(e) => handleUpdatePreference('autoAcceptNonPriceSupplierChangesOnly', e.target.checked)}
                                />
                            </Flex>
                        </Box>
                    )}
                </SettingsRow>

                <FlagMarginBelowTable
                    title="Acceptable margin threshold"
                    description="If the margin on the selected part is below this amount, the part will automatically be flagged.  The default is used when no threshold is configured for a part type."
                    entries={localData.flagMarginBelowByPart}
                    savedEntries={savedOrdering.flagMarginBelowByPart}
                    onChange={persistFlagMarginByPart}
                    modalTitle="Add Part Types"
                />
            </SettingsCard>

            <SettingsCard
                title="Margin thresholds"
                description="The minimum gross margin Yaxle's needs before it will place an order without human review."
            >
                <SettingsRow
                    label="Margin threshold (%)"
                    description="This is the minimum gross margin to allow Yaxle to place an order without review."
                    control={
                        <SettingsNumberField
                            value={Math.round(localData.flagMarginThreshhold * 100)}
                            onChange={(next) =>
                                handleUpdatePreference('flagMarginThreshhold', next / 100)
                            }
                            allowEmpty={false}
                            min={0}
                            max={100}
                            width="100px"
                            unit="%"
                            ariaLabel="Margin threshold"
                        />
                    }
                />

                <SettingsRow
                    label="Non-DRP margin threshold (%)"
                    description="The minimum gross margin for non-DRP jobs to allow auto ordering without review."
                    control={
                        <SettingsNumberField
                            value={Math.round(localData.nonDrpFlagMarginThreshold * 100)}
                            onChange={(next) =>
                                handleUpdatePreference('nonDrpFlagMarginThreshold', next / 100)
                            }
                            allowEmpty={false}
                            min={0}
                            max={100}
                            width="100px"
                            unit="%"
                            ariaLabel="Non-DRP margin threshold"
                        />
                    }
                />

                <OemOverridesTable
                    title="Margin thresholds by make (optional)"
                    description="Override the DRP and non-DRP thresholds by make for all orders."
                    columns={MARGIN_THRESHOLD_COLUMNS}
                    oems={localOems}
                    oemOptions={oemOptions}
                    onChange={handleOemsChange}
                    emptyLabel="No per-make thresholds configured. Every make uses the org-wide thresholds above."
                    modalTitle="Add Makes"
                />
            </SettingsCard>

            <SettingsAddItemsModal
                isOpen={isLocationModalOpen}
                onClose={() => setIsLocationModalOpen(false)}
                modalTitle="Disable auto ordering"
                searchPlaceholder="Search locations..."
                searchTerm={locationSearch}
                onSearchTermChange={setLocationSearch}
                emptySearchLabel={locationSearch ? 'No locations found' : 'All locations already overridden'}
                items={filteredLocations.map((location) => ({
                    id: location._id,
                    label: location.title,
                }))}
                pendingIds={pendingLocationIds}
                onTogglePending={togglePendingLocation}
                selectedCountLabel={
                    pendingLocationIds.size > 0
                        ? `${pendingLocationIds.size} location${pendingLocationIds.size === 1 ? '' : 's'} selected`
                        : null
                }
                onAdd={handleAddDisabledLocations}
            />
        </VStack>
    );
};

export default Ordering;
